import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/lib/auth";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [timedOut, setTimedOut] = useState(false);
  const [error, setError] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!sid) {
      setError("Missing session id — the payment link is invalid.");
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        setStatus(r.data);
        if (r.data.payment_status === "paid") {
          setTimeout(() => nav("/app"), 1400);
          return;
        }
        if (attempts++ < 30) setTimeout(poll, 2000);
        else setTimedOut(true);
      } catch (e) {
        if (attempts++ < 30) setTimeout(poll, 2000);
        else {
          setTimedOut(true);
          setError(e?.response?.data?.detail || null);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sid, nav]);

  const paid = status.payment_status === "paid";

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "#f9f8f6" }} data-testid="payment-success-page">
      <div className="max-w-md w-full text-center rounded-2xl border border-[#e5e2da] bg-white p-10">
        {error ? (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-[#d4a373]" strokeWidth={1.5} />
            <h1 className="font-serif-display text-3xl mt-6" data-testid="payment-success-error">Something's off.</h1>
            <p className="text-[#5c5c5c] mt-3 text-sm">{error}</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/plans" className="rounded-full bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6] px-5 py-2 text-sm" data-testid="payment-back-plans">Back to plans</Link>
              <Link to={user ? "/app" : "/"} className="rounded-full border border-[#e5e2da] px-5 py-2 text-sm text-[#3c3c3c] hover:bg-[#efece5]" data-testid="payment-home-link">{user ? "Dashboard" : "Home"}</Link>
            </div>
          </>
        ) : paid ? (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#163325]" strokeWidth={1.5} />
            <h1 className="font-serif-display text-4xl mt-6" data-testid="payment-success-title">You're in.</h1>
            <p className="text-[#5c5c5c] mt-3">Your 7-day free trial has started{user ? `, ${user.name.split(" ")[0]}` : ""}. We'll charge your card on day 7 unless you cancel.</p>
            <p className="text-[#8c9b86] text-sm mt-6">Redirecting to your dashboard…</p>
            <Link to="/app" className="mt-4 inline-block text-xs text-[#163325] underline underline-offset-4" data-testid="payment-continue-link">Continue now</Link>
          </>
        ) : timedOut ? (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-[#d4a373]" strokeWidth={1.5} />
            <h1 className="font-serif-display text-3xl mt-6" data-testid="payment-timeout-title">Still waiting on Stripe.</h1>
            <p className="text-[#5c5c5c] mt-3 text-sm">Your card details were accepted but we haven't heard back yet. You can safely head to your dashboard — your subscription status will update as soon as Stripe confirms.</p>
            <Link to={user ? "/app" : "/login"} className="mt-5 inline-block rounded-full bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6] px-5 py-2 text-sm" data-testid="payment-continue-btn">Continue to dashboard</Link>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[#163325] animate-spin" data-testid="payment-loading-spinner" />
            <h1 className="font-serif-display text-3xl mt-6">Finalising your trial…</h1>
            <p className="text-[#5c5c5c] mt-3 text-sm">This usually takes a couple of seconds.</p>
            <Link to={user ? "/app" : "/"} className="mt-6 inline-block text-xs text-[#8c9b86] underline underline-offset-4" data-testid="payment-skip-link">Skip ahead</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "#f9f8f6" }} data-testid="payment-cancel-page">
      <div className="max-w-md w-full text-center rounded-2xl border border-[#e5e2da] bg-white p-10">
        <XCircle className="w-14 h-14 mx-auto text-[#8c9b86]" strokeWidth={1.5} />
        <h1 className="font-serif-display text-4xl mt-6">Checkout cancelled.</h1>
        <p className="text-[#5c5c5c] mt-3">No card was charged. You can start your 7-day trial anytime.</p>
        <div className="mt-6 flex gap-3 justify-center">
          <Link to="/plans" className="rounded-full bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6] px-5 py-2 text-sm" data-testid="cancel-back-to-plans">Back to plans</Link>
          <Link to="/" className="rounded-full border border-[#e5e2da] px-5 py-2 text-sm text-[#3c3c3c] hover:bg-[#efece5]" data-testid="cancel-home">Home</Link>
        </div>
      </div>
    </div>
  );
}
